/**
 * Pure, side-effect-free helpers for the upload-queue feature.
 *
 * - `computeProgress` guards against a zero total by returning 0 when
 *   `total` is missing or non-positive. Result is clamped to [0, 100].
 * - `formatBytes` renders byte counts as "0 B", "1.0 KB", "1.4 MB", etc.
 * - `fileIconForMime` maps a MIME type to a Lucide icon component for the row.
 *
 * No React imports here — these helpers are consumed by both the store
 * (`computeProgress`) and UI components.
 */
import type { LucideIcon } from 'lucide-react';
import { FileAudioIcon, FileIcon, FileImageIcon, FileTextIcon, FileVideoIcon } from 'lucide-react';

/**
 * Progress = round((loaded / total) * 100) when total > 0; else 0.
 * Clamped to [0, 100] so over-reported uploads cannot exceed the bar.
 */
export function computeProgress(loaded: number, total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  const pct = Math.round((loaded / total) * 100);
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

/**
 * Human-readable byte size. One decimal place above bytes. Lower-case unit
 * with a single space separator so it concatenates cleanly into row labels.
 */
export function formatBytes(bytes: number): string {
  // eslint-disable-next-line i18next/no-literal-string -- byte-unit symbol, identical in every locale
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < KB) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / KB).toFixed(1)} KB`;
  if (bytes < GB) return `${(bytes / MB).toFixed(1)} MB`;
  return `${(bytes / GB).toFixed(1)} GB`;
}

/**
 * Map a MIME type to a Lucide icon component. Falls back to a generic file
 * icon for unknown or empty MIME types. `application/pdf` is treated as a
 * text-bearing document for visual continuity with FileListItem precedent.
 */
export function fileIconForMime(mimeType: string): LucideIcon {
  if (!mimeType) return FileIcon;
  if (mimeType.startsWith('image/')) return FileImageIcon;
  if (mimeType.startsWith('audio/')) return FileAudioIcon;
  if (mimeType.startsWith('video/')) return FileVideoIcon;
  if (mimeType.startsWith('text/') || mimeType === 'application/pdf') return FileTextIcon;
  return FileIcon;
}
